import React from "react";
import { Box, Card, CardContent, Typography, useTheme } from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import {
  getSignColor,
  formatCurrency,
  formatPercentColored,
} from "../../PerformanceDashboard/utility";
import {
  v2CardBg,
  v2CardBorder,
  v2LabelSx,
  v2Accent,
  v2AccentSoftBg,
} from "../v2Theme";

const HeroTile = ({ title, value, color, theme, icon: Icon, compact = false }) => {
  const isDark = theme.palette.mode === "dark";
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        height: "100%",
        width: "100%",
        borderColor: v2CardBorder(theme),
        backgroundColor: v2CardBg(theme),
        boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
        color: "text.primary",
      }}
    >
      <CardContent
        sx={{
          py: 2,
          px: compact ? 1.5 : 2.25,
          "&:last-child": { pb: 2 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                fontSize: compact ? "0.78rem" : "0.85rem",
                ...v2LabelSx(theme),
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mt: 0.75,
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1.15,
                color: color || "text.primary",
                fontSize: compact
                  ? { xs: "1.1rem", md: "1.25rem" }
                  : { xs: "1.2rem", md: "1.4rem" },
              }}
            >
              {value}
            </Typography>
          </Box>
          {Icon && (
            <Box
              sx={{
                flexShrink: 0,
                p: 1,
                borderRadius: 1.5,
                backgroundColor: v2AccentSoftBg(theme),
                color: v2Accent(theme),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon sx={{ fontSize: 22 }} />
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const PERIOD_CARDS = [
  { key: "yest", title: "P(L)% Yest" },
  { key: "today", title: "P(L)% Today" },
  { key: "mtd", title: "P(L)% MTD" },
  { key: "qtd", title: "P(L)% QTD" },
  { key: "ytd", title: "P(L)% YTD" },
  { key: "itd", title: "P(L)% ITD" },
];

const formatHeroPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return formatPercentColored(Number(num.toFixed(2)));
};

const PortfolioHero = ({ totalAum, totalPL, periodPercents = {} }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          sm: "repeat(2, minmax(0, 1fr))",
          md: "repeat(4, minmax(0, 1fr))",
          lg: "minmax(210px, 1.55fr) minmax(130px, 1fr) repeat(6, minmax(0, 0.82fr))",
        },
        gap: 1.5,
        alignItems: "stretch",
      }}
    >
      <HeroTile
        theme={theme}
        title="Total AUM"
        value={formatCurrency(totalAum) || "—"}
        icon={AccountBalanceWalletOutlinedIcon}
      />
      <HeroTile
        theme={theme}
        title="P(L) Today"
        value={formatCurrency(totalPL) || "—"}
        color={getSignColor(totalPL, theme)}
        compact
      />
      {PERIOD_CARDS.map((period) => {
        const value = Number(periodPercents[period.key]);
        const hasValue = Number.isFinite(value);
        return (
          <HeroTile
            key={period.key}
            theme={theme}
            title={period.title}
            value={hasValue ? formatHeroPercent(value) : "—"}
            color={hasValue ? getSignColor(value, theme) : undefined}
            compact
          />
        );
      })}
    </Box>
  );
};

export default PortfolioHero;
